import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Button, 
    Table, 
    Modal, 
    Form, 
    Input, 
    Select, 
    Switch, 
    message, 
    Space, 
    Tag, 
    Popconfirm,
    Alert,
    Divider,
    Row,
    Col,
    Typography
} from 'antd';
import { 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    SendOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    SettingOutlined
} from '@ant-design/icons';
import api from '../../../services/api';
import { getStatusColor } from '../../../utils/ui';

const { Title, Text } = Typography;
const { Option } = Select;

function EmailConfiguration() {
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [form] = Form.useForm();
    const [drivers, setDrivers] = useState({});
    const [templates, setTemplates] = useState({});

    useEffect(() => {
        loadConfigurations();
        loadDrivers();
        loadTemplates();
    }, []);

    const loadConfigurations = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetEmailConfigurations();
            setConfigurations(response.data || []);
        } catch (error) {
            message.error('Failed to load email configurations');
        } finally {
            setLoading(false);
        }
    };

    const loadDrivers = async () => {
        try {
            const response = await api.adminGetEmailDrivers();
            setDrivers(response.data || {});
        } catch (error) {
            console.error('Failed to load drivers:', error);
        }
    };

    const loadTemplates = async () => {
        try {
            const response = await api.adminGetEmailTemplates();
            setTemplates(response.data || {});
        } catch (error) {
            console.error('Failed to load templates:', error);
        }
    };

    const openModal = (config = null) => {
        setEditingConfig(config);
        if (config) {
            form.setFieldsValue({
                ...config,
                password: '', // Don't show existing password
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                driver: 'smtp',
                is_active: true,
                is_default: false,
            });
        }
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            
            // Remove empty password field if not provided
            if (!values.password) {
                delete values.password;
            }

            if (editingConfig) {
                await api.adminUpdateEmailConfiguration(editingConfig.id, values);
                message.success('Email configuration updated successfully');
            } else {
                await api.adminCreateEmailConfiguration(values);
                message.success('Email configuration created successfully');
            }
            
            setModalVisible(false);
            form.resetFields();
            loadConfigurations();
        } catch (error) {
            message.error(error.message || 'Failed to save email configuration');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.adminDeleteEmailConfiguration(id);
            message.success('Email configuration deleted successfully');
            loadConfigurations();
        } catch (error) {
            message.error(error.message || 'Failed to delete email configuration');
        }
    };

    const handleTest = async (id) => {
        try {
            setLoading(true);
            const response = await api.adminTestEmailConfiguration(id);
            if (response.success) {
                message.success('Test email sent successfully!');
            } else {
                message.error('Test email failed to send');
            }
        } catch (error) {
            message.error(error.message || 'Failed to send test email');
        } finally {
            setLoading(false);
        }
    };

    const handleSetDefault = async (id) => {
        try {
            setLoading(true);
            await api.adminSetDefaultEmailConfiguration(id);
            message.success('Email configuration set as default successfully');
            loadConfigurations();
        } catch (error) {
            message.error(error.message || 'Failed to set default configuration');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <Text strong>{text}</Text>
                    {record.is_default && <Tag color="blue">Default</Tag>}
                </Space>
            ),
        },
        {
            title: 'Driver',
            dataIndex: 'driver',
            key: 'driver',
            render: (driver) => <Tag color="purple">{driver.toUpperCase()}</Tag>,
        },
        {
            title: 'From Address',
            dataIndex: 'from_address',
            key: 'from_address',
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={getStatusColor(isActive ? 'success' : 'cancelled')}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<SendOutlined />}
                        onClick={() => handleTest(record.id)}
                        loading={loading}
                    >
                        Test
                    </Button>
                    <Button 
                        size="small" 
                        icon={<EditOutlined />}
                        onClick={() => openModal(record)}
                    >
                        Edit
                    </Button>
                    {!record.is_default && (
                        <Button 
                            size="small" 
                            type="primary"
                            onClick={() => handleSetDefault(record.id)}
                            loading={loading}
                        >
                            Set Default
                        </Button>
                    )}
                    {!record.is_default && (
                        <Popconfirm
                            title="Are you sure you want to delete this configuration?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button 
                                size="small" 
                                danger
                                icon={<DeleteOutlined />}
                            >
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const renderDriverFields = (driver) => {
        switch (driver) {
            case 'smtp':
                return (
                    <>
                        <Form.Item
                            label="SMTP Host"
                            name="host"
                            rules={[{ required: true, message: 'Please enter SMTP host' }]}
                        >
                            <Input placeholder="smtp.gmail.com" />
                        </Form.Item>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Port"
                                    name="port"
                                    rules={[{ required: true, message: 'Please enter port' }]}
                                >
                                    <Input type="number" placeholder="587" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Encryption"
                                    name="encryption"
                                >
                                    <Select placeholder="Select encryption">
                                        <Option value="tls">TLS</Option>
                                        <Option value="ssl">SSL</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item
                            label="Username"
                            name="username"
                            rules={[{ required: true, message: 'Please enter username' }]}
                        >
                            <Input placeholder="your-email@gmail.com" />
                        </Form.Item>
                        <Form.Item
                            label="Password"
                            name="password"
                            rules={[{ required: !editingConfig, message: 'Please enter password' }]}
                        >
                            <Input.Password placeholder="App password or account password" />
                        </Form.Item>
                    </>
                );
            case 'mailgun':
                return (
                    <>
                        <Form.Item
                            label="Domain"
                            name={['settings', 'domain']}
                            rules={[{ required: true, message: 'Please enter Mailgun domain' }]}
                        >
                            <Input placeholder="mg.yourdomain.com" />
                        </Form.Item>
                        <Form.Item
                            label="Secret Key"
                            name={['settings', 'secret']}
                            rules={[{ required: true, message: 'Please enter Mailgun secret' }]}
                        >
                            <Input.Password placeholder="key-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
                        </Form.Item>
                    </>
                );
            case 'ses':
                return (
                    <>
                        <Form.Item
                            label="Access Key"
                            name={['settings', 'key']}
                            rules={[{ required: true, message: 'Please enter AWS access key' }]}
                        >
                            <Input placeholder="AKIAIOSFODNN7EXAMPLE" />
                        </Form.Item>
                        <Form.Item
                            label="Secret Key"
                            name={['settings', 'secret']}
                            rules={[{ required: true, message: 'Please enter AWS secret key' }]}
                        >
                            <Input.Password placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY" />
                        </Form.Item>
                        <Form.Item
                            label="Region"
                            name={['settings', 'region']}
                            rules={[{ required: true, message: 'Please enter AWS region' }]}
                        >
                            <Input placeholder="us-east-1" />
                        </Form.Item>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Email Configuration</Title>
                <Text type="secondary">
                    Configure email settings for sending notifications, contact forms, and newsletters.
                </Text>
            </div>

            <Card
                title="Email Configurations"
                extra={
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={() => openModal()}
                    >
                        Add Configuration
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={configurations}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                    }}
                />
            </Card>

            <Modal
                title={editingConfig ? 'Edit Email Configuration' : 'Add Email Configuration'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label="Configuration Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter configuration name' }]}
                    >
                        <Input placeholder="e.g., Gmail SMTP, Mailgun Production" />
                    </Form.Item>

                    <Form.Item
                        label="Email Driver"
                        name="driver"
                        rules={[{ required: true, message: 'Please select email driver' }]}
                    >
                        <Select placeholder="Select email driver">
                            {Object.entries(drivers).map(([key, value]) => (
                                <Option key={key} value={key}>{value}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item shouldUpdate={(prev, cur) => prev.driver !== cur.driver} noStyle>
                        {() => {
                            const driver = form.getFieldValue('driver');
                            return renderDriverFields(driver);
                        }}
                    </Form.Item>

                    <Divider />

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="From Address"
                                name="from_address"
                                rules={[
                                    { required: true, message: 'Please enter from address' },
                                    { type: 'email', message: 'Please enter valid email' }
                                ]}
                            >
                                <Input placeholder="noreply@yourdomain.com" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="From Name"
                                name="from_name"
                                rules={[{ required: true, message: 'Please enter from name' }]}
                            >
                                <Input placeholder="Spoon & Spice" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Reply-To Address"
                                name="reply_to_address"
                                rules={[{ type: 'email', message: 'Please enter valid email' }]}
                            >
                                <Input placeholder="support@yourdomain.com" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Reply-To Name"
                                name="reply_to_name"
                            >
                                <Input placeholder="Support Team" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Active"
                                name="is_active"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Default"
                                name="is_default"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="flex justify-end gap-2 mt-6">
                        <Button onClick={() => setModalVisible(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {editingConfig ? 'Update' : 'Create'} Configuration
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}

export default EmailConfiguration;
